import { type ThemeProps } from '@kijiji/theme'
import styled from 'styled-components'

import { NextButton, PrevButton } from '@/components/shared/carousel-buttons/CarouselButtons'
import { MEDIA_QUERIES } from '@/ui/constants/mediaQueries'

const AD_HEIGHT_DESKTOP = '90px'
const IMAGES_GAP = '2.4rem'
const MODAL_HEADER_HEIGHT = '7.1rem'
const R2S_WIDTH = '41rem'
const THUMBNAIL_HEIGHT = '5.6rem'
const THUMBNAIL_SLIDE_SPACING = '0.8rem'

/**
 * The carousel needs some specific sizing to comply to. It doesn't respect the parent's
 * if doesn't have a defined height.
 *
 * This will ensure the carousel fits in the height of the screen without scrolling.
 *
 * This calculation is the whole screen minus the ad size, the gap between ad and carousel,
 * the header of the modal, top and bottom paddings of the modal, and the space between the main image and
 * thumbnails.
 */
const MAX_GALLERY_SIZE = `calc(100vh - ${AD_HEIGHT_DESKTOP} - ${MODAL_HEADER_HEIGHT} - 3.2rem - 2.4rem)`

export const ImageGalleryLayout = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0;

  ${MEDIA_QUERIES(theme).medium} {
    display: grid;
    gap: 3.2rem;
    grid-template-columns: 1fr minmax(41rem, auto);
    padding: 0 ${theme.spacing.default} ${theme.spacing.default};
  }

  ${MEDIA_QUERIES(theme).large} {
    padding: 0 ${theme.spacing.xLarge} ${theme.spacing.default};
  }
`
)

export const GalleryImagesSection = styled.div(
  ({ theme }) => `
  position: relative;
  width: 100vw;
  
  ${MEDIA_QUERIES(theme).medium} {
    display: grid;
    gap: ${IMAGES_GAP};
    grid-template-rows: minmax(0, calc(${MAX_GALLERY_SIZE} - ${THUMBNAIL_HEIGHT} - ${IMAGES_GAP})) ${THUMBNAIL_HEIGHT};
    width: calc(100vw - ${R2S_WIDTH} - 6.4rem);
  }
`
)

export const GalleryMainImageSection = styled.div(
  ({ theme }) => `
  height: auto;
  padding-bottom: ${IMAGES_GAP};
  position: relative;
  width: inherit;

  ${MEDIA_QUERIES(theme).medium} {
    height: 100%;
    padding-bottom: 0;
  }
`
)

export const GalleryThumbnailsSection = styled.div`
  height: 100%;
  max-height: ${THUMBNAIL_HEIGHT};
  width: inherit;

  & > div {
    overflow: hidden;
  }
`

export const GalleryThumbnailSlides = styled.div`
  display: flex;
  justify-content: center;
`

const ThumbnailSelectedStyle = (theme: ThemeProps) => `
  padding: .6rem;

  &::after {
    content: "";
    border-radius: ${theme.borderRadius.medium};
    border: .3rem solid ${theme.colors.purple.primary};
    height: calc(100% - .6rem);
    left: 0;
    opacity: 1;
    position: absolute;
    top: 0;
    width: calc(100% - .6rem);
  }
`

// eslint-disable-next-line @kijiji/prefer-button-component
export const GalleryThumbnailSlide = styled.button<{ selected?: boolean }>(
  ({ theme, selected }) => `
  flex: 0 0 7.4rem;
  height: ${THUMBNAIL_HEIGHT};
  margin-left: ${THUMBNAIL_SLIDE_SPACING};
  min-width: 0;
  transition: opacity .3s ease-in-out;
  width: 7.4rem;
  position: relative;

  ${selected ? ThumbnailSelectedStyle(theme) : ''}

  &:hover {
    opacity: 1;
  }

  & img {
    border-radius: ${theme.borderRadius.medium};
    height: 100%;
    object-fit: cover;
    width: 100%;
  }
`
)

export const GalleryR2SSection = styled.div(
  ({ theme }) => `
  border-radius: 0 0 ${theme.borderRadius.medium} ${theme.borderRadius.medium};
  border: .1rem solid ${theme.colors.grey.light4};
  box-shadow: 0 .2rem .4rem #2d2f3b1f;
  display: none;
  height: fit-content;
  padding: ${theme.spacing.default};
  width: 100%;

  ${MEDIA_QUERIES(theme).medium}{
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.default};
    width: calc(${R2S_WIDTH} - ${theme.spacing.default});
  }
`
)

export const ImageGalleryMainCarousel = styled.div(
  ({ theme }) => `
  height: inherit;
  overflow: hidden;
  width: 100%;

  & > div {
    display: flex;
    height: 100%;
    touch-action: pan-y pinch-zoom;
  }

  ${MEDIA_QUERIES(theme).medium} {
    border-radius: ${theme.borderRadius.medium};
  }
`
)

// eslint-disable-next-line @kijiji/prefer-image-custom
export const ImageGalleryPictureSlideStyled = styled.img`
  flex: 0 0 100%;
  min-width: 0;
  transform: translate3d(0, 0, 0);
  object-fit: contain;
`

const galleryButtonStyle = ({ theme }: { theme: ThemeProps }) => `
  background: ${theme.colors.white};
  display: none;
  height: 3.5rem;
  position: absolute;
  width: 3.5rem;

  & > svg {
    stroke: ${theme.colors.grey.primary};
  }

  ${MEDIA_QUERIES(theme).medium}{
    display: block;
  }
`

export const ImageGalleryYoutubeSlideStyled = styled.div`
  flex: 0 0 100%;
  margin: 0;
  min-width: 0;
  transform: translate3d(0, 0, 0);
`

export const ImageGalleryVirtualSlideStyled = styled.div`
  flex: 0 0 100%;
  transform: translate3d(0, 0, 0);
`

export const ImageGalleryPrevButton = styled(PrevButton)(
  ({ theme }) => `
  && {
    ${galleryButtonStyle({ theme })}
    left: 2rem;
  }
`
)

export const ImageGalleryNextButton = styled(NextButton)(
  ({ theme }) => `
  && {
    ${galleryButtonStyle({ theme })}
    right: 2rem;
  }
`
)
